import { Dispatch } from 'redux';
import { Subscription } from 'rxjs';

import * as AuthActions from '../actions/auth.actions';
import { Action } from '../types/action';
import { AppState } from '../types/app-state';
import { UserRegistrationDetails } from '../types/user-registration-details';
import {
  createExistingEmailChecker,
  ExistingEmailChecker,
  generateVerificationCode,
  confirmVerificationCode,
  registerUser,
  createExistingDomainChecker,
  ExistingDomainChecker,
  registerOrganisation
} from '../services/register';
import { validateEmail } from '../utils/validateEmail';
import { OrganisationRegistrationDetails } from '../types/organisation-registration-details';
import * as Constants from './register.constants';

export interface CheckExistingEmailDoneAction extends Action {
  exists: boolean;
}

export interface SetErrorMessageAction extends Action {
  message: string | null;
}

export interface SetVerificationDetailsType {
  token: string;
  code: string;
  codeVerified: boolean;
}

export interface SetVerificationDetailsAction extends Action, Partial<SetVerificationDetailsType> { }

export interface SetUserDetailsAction extends Action, Partial<UserRegistrationDetails> { }

export interface SetOrganisationDetailsAction extends Action, Partial<OrganisationRegistrationDetails> { }

export interface CheckExistingDomainDoneAction extends Action {
  exists: boolean;
}

export interface OrganisationRegistrationDone extends Action {
  organisationId: string;
}

let checkEmail: ExistingEmailChecker | undefined;
let checkEmailOutletSub: Subscription;
let checkDomain: ExistingDomainChecker | undefined;
let checkDomainOutletSub: Subscription;

export function Initialize() {
  return async (dispatch: Dispatch<any>) => {
    dispatch({ type: Constants.INITIALIZE });

    if (!checkEmail) {
      checkEmail = createExistingEmailChecker();
      checkEmailOutletSub = checkEmail.outlet.subscribe((response: any) => {
        const { status, data } = response;
        if (status === 1 && data) {
          dispatch(CheckExistingEmailDone(data.existing));
        }
      });
    }

    if (!checkDomain) {
      checkDomain = createExistingDomainChecker();
      checkDomainOutletSub = checkDomain.outlet.subscribe((response: any) => {
        const { status, data } = response;
        if (status === 1 && data) {
          dispatch(CheckExistingDomainDone(data.existing));
        }
      });
    }

  };
}

export function CheckExistingEmail(email: string) {
  return async (dispatch: Dispatch<any>, getState: () => AppState) => {
    if (validateEmail(email)) {
      if (checkEmail) {
        dispatch({ type: Constants.CHECK_EXISTING_EMAIL });
        checkEmail.send({ email });
      }
    } else {
      if (getState().register.emailExisting) {
        dispatch(CheckExistingEmailDone(false));
      }
    }
  };
}

export function CheckExistingEmailDone(exists: boolean): CheckExistingEmailDoneAction {
  return {
    type: Constants.CHECK_EXISTING_EMAIL_DONE,
    exists
  };
}

export function Destroy() {
  return async (dispatch: Dispatch<any>) => {
    dispatch({ type: Constants.DESTROY });
    if (checkEmailOutletSub) {
      checkEmailOutletSub.unsubscribe();
    }
    if (checkEmail) {
      checkEmail = undefined;
    }
    if (checkDomainOutletSub) {
      checkDomainOutletSub.unsubscribe();
    }
    if (checkDomain) {
      checkDomain = undefined;
    }
  };
}

export function SetErrorMessage(message: string | null): SetErrorMessageAction {
  return {
    type: Constants.SET_ERROR_MESSAGE,
    message
  };
}

export function GetVerificationCode(email: string) {
  return async (dispatch: Dispatch<any>) => {
    try {
      dispatch({ type: Constants.GET_VERIFICATION_CODE });
      dispatch(SetUserDetails({ email }));
      const { status, message, data } = await generateVerificationCode(email);
      if (status !== 1) {
        throw new Error(status === -2 ? 'A server error occurred' : message);
      }
      dispatch(SetVerificationDetails({ ...data }));
      dispatch(SetErrorMessage(null));
    } catch (error) {
      dispatch(SetErrorMessage(error.message));
    }
  };
}

export function SetVerificationDetails(details: Partial<SetVerificationDetailsType>): SetVerificationDetailsAction {
  return {
    type: Constants.SET_VERIFICATION_DETAILS,
    ...details
  };
}

export function SetUserDetails(details: Partial<UserRegistrationDetails>): SetUserDetailsAction {
  return {
    type: Constants.SET_USER_DETAILS,
    ...details
  };
}

export function SetOrganisationDetails(details: Partial<OrganisationRegistrationDetails>)
  : SetOrganisationDetailsAction {
  return {
    type: Constants.SET_ORGANISATION_DETAILS,
    ...details
  };
}

export function ConfirmVerificationCode(code: string) {
  return async (dispatch: Dispatch<any>, getState: () => AppState) => {
    try {
      dispatch({ type: Constants.CONFIRM_VERIFICATION_CODE });
      const { email, token } = getState().register;
      if (!email || !token) {
        throw new Error('Email and / or token is missing.');
      }

      const { status, message } = await confirmVerificationCode(email, code, token);
      if (status !== 1) {
        throw new Error(status === -2 ? 'A server error occurred' : message);
      } else {
        dispatch(SetVerificationDetails({ codeVerified: true, code }));
        dispatch(SetErrorMessage(null));
      }
    } catch (error) {
      dispatch(SetErrorMessage(error.message));
    }
  };
}

export function RegisterUser() {
  return async (dispatch: Dispatch<any>, getState: () => AppState) => {
    try {
      dispatch({ type: Constants.REGISTER_USER });
      const {
        code,
        token,
        email,
        password,
        firstName,
        lastName
        // username
      } = getState().register;

      if (!(
        code &&
        token &&
        email &&
        password &&
        firstName &&
        lastName
        // username
      )) {
        dispatch(SetErrorMessage('User details are not complete'));
        return;
      }

      const { status, message } = await registerUser({
        code,
        token,
        email,
        password,
        firstName,
        lastName
        // username 
      });

      if (status !== 1) {
        throw new Error(status === -2 ? 'A server error occurred' : message);
      }

      await dispatch(AuthActions.Login(email, password));
      dispatch(SetErrorMessage(null));

    } catch (error) {
      dispatch(SetErrorMessage(error.message));
    }
  };
}

export function CheckExistingDomain(domain: string) {
  return async (dispatch: Dispatch<any>, getState: () => AppState) => {
    try {
      const { token } = getState().auth;

      if (checkDomain && domain && token) {
        dispatch({ type: Constants.CHECK_EXISTING_DOMAIN });
        checkDomain.send({ organisationDomain: domain.trim(), token });
      }
    } catch (error) {
      dispatch(SetErrorMessage(error.message));
    }
  };
}

export function CheckExistingDomainDone(exists: boolean): CheckExistingDomainDoneAction {
  return {
    type: Constants.CHECK_EXISTING_DOMAIN_DONE,
    exists
  };
}

export function RegisterOrganisation() {
  return async (dispatch: Dispatch<any>, getState: () => AppState) => {
    try {
      dispatch({ type: Constants.REGISTER_ORGANISATION });

      const token = getState().auth.token as string;
      const { organisationName, organisationDomain } = getState().register;

      if (!organisationName || !organisationDomain) {
        throw new Error('Client error: organisation details are missing.');
      }

      const { status, message, data } = await registerOrganisation({
        token,
        organisationDomain: (organisationDomain + '').trim(),
        organisationName
      });

      if (status !== 1 || !data) {
        throw new Error(status === -2 ? 'A server error occurred' : message);
      }

      dispatch(RegisterOrganisationDone(data.organisation_id));
      dispatch(SetErrorMessage(null));

    } catch (error) {
      dispatch(SetErrorMessage(error.message));
    }
  };
}

export function RegisterOrganisationDone(organisationId: string): OrganisationRegistrationDone {
  return {
    type: Constants.REGISTER_ORGANISATION_DONE,
    organisationId
  };
}

export type All
  = Action
  | CheckExistingEmailDoneAction
  | SetErrorMessageAction
  | SetVerificationDetailsAction
  | SetUserDetailsAction
  | SetOrganisationDetailsAction
  | CheckExistingDomainDoneAction
  | OrganisationRegistrationDone;